import React, { useEffect, useState, useCallback } from 'react';
import api, { isSilentNetworkError } from '../../services/api';
import styles from './FeedbackManagement.module.css';

const TYPE_LABELS = {
  suggestion: '建议',
  bug: '问题反馈',
  feature: '功能请求',
  book: '书籍请求',
  other: '其他',
};

const STATUS_LABELS = {
  pending: '待处理',
  processing: '处理中',
  resolved: '已解决',
  ignored: '已忽略',
};

const STATUS_OPTIONS = ['pending', 'processing', 'resolved', 'ignored'];

const PAGE_SIZE = 30;

function FeedbackManagement() {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [filterStatus, setFilterStatus] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const fetchFeedbacks = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        offset: String(page * PAGE_SIZE),
        limit: String(PAGE_SIZE),
      });
      if (filterStatus) params.append('status', filterStatus);

      const { data } = await api.get(`/feedbacks/?${params}`);
      setFeedbacks(data);
    } catch (err) {
      console.error('获取反馈列表失败:', err);
    } finally {
      setLoading(false);
    }
  }, [page, filterStatus]);

  useEffect(() => {
    fetchFeedbacks();
  }, [fetchFeedbacks]);

  const handleSearch = () => {
    setPage(0);
    fetchFeedbacks();
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await api.patch(`/feedbacks/${id}/status`, { status: newStatus });
      setFeedbacks((prev) =>
        prev.map((fb) => (fb.id === id ? { ...fb, status: newStatus } : fb))
      );
    } catch (err) {
      if (isSilentNetworkError(err)) return;
      console.error('更新状态失败:', err);
      alert('更新状态失败');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('确定删除这条反馈？')) return;
    try {
      await api.delete(`/feedbacks/${id}`);
      setFeedbacks((prev) => prev.filter((fb) => fb.id !== id));
    } catch (err) {
      if (isSilentNetworkError(err)) return;
      console.error('删除失败:', err);
      alert('删除失败');
    }
  };

  const formatDate = (isoStr) => {
    if (!isoStr) return '-';
    return new Intl.DateTimeFormat('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(isoStr));
  };

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const pendingCount = feedbacks.filter((f) => f.status === 'pending').length;
  const resolvedCount = feedbacks.filter((f) => f.status === 'resolved').length;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h2>用户反馈管理</h2>
          <p className={styles.subtitle}>查看和处理用户提交的反馈</p>
        </div>
      </div>

      {/* 筛选栏 */}
      <div className={styles.filters}>
        <select
          className={styles.filterSelect}
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="">全部状态</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABELS[s]}
            </option>
          ))}
        </select>
        <button className={styles.searchBtn} onClick={handleSearch}>
          查询
        </button>
      </div>

      {/* 小统计 */}
      <div className={styles.miniStats}>
        <span className={styles.statPending}>● 待处理 {pendingCount}</span>
        <span className={styles.statResolved}>● 已解决 {resolvedCount}</span>
        <span className={styles.statTotal}>共 {feedbacks.length} 条</span>
      </div>

      {/* 反馈列表 */}
      {loading ? (
        <p className={styles.hint}>加载中…</p>
      ) : feedbacks.length === 0 ? (
        <p className={styles.hint}>暂无反馈</p>
      ) : (
        <div className={styles.list}>
          {feedbacks.map((fb) => {
            const isExpanded = expandedId === fb.id;
            return (
              <div
                key={fb.id}
                className={`${styles.card} ${styles[`card_${fb.status}`] || ''}`}
              >
                {/* 卡片头部 */}
                <div className={styles.cardHeader} onClick={() => toggleExpand(fb.id)}>
                  <div className={styles.cardLeft}>
                    <span className={`${styles.typeBadge} ${styles[`type_${fb.type}`] || ''}`}>
                      {TYPE_LABELS[fb.type] || fb.type}
                    </span>
                    <span className={styles.cardTitle}>{fb.title}</span>
                  </div>
                  <div className={styles.cardRight}>
                    <span className={`${styles.statusBadge} ${styles[`status_${fb.status}`]}`}>
                      {STATUS_LABELS[fb.status] || fb.status}
                    </span>
                    <span className={styles.cardTime}>{formatDate(fb.created_at)}</span>
                    <span className={styles.expandIcon}>{isExpanded ? '▾' : '▸'}</span>
                  </div>
                </div>

                {/* 展开详情 */}
                {isExpanded && (
                  <div className={styles.cardBody}>
                    <div className={styles.meta}>
                      {fb.username ? (
                        <span>用户: <strong>{fb.username}</strong> (#{fb.user_id})</span>
                      ) : (
                        <span>匿名用户</span>
                      )}
                      {fb.contact && <span>联系方式: {fb.contact}</span>}
                    </div>
                    <div className={styles.content}>{fb.content}</div>
                    <div className={styles.actions}>
                      <label className={styles.statusLabel}>状态:</label>
                      <select
                        className={styles.statusSelect}
                        value={fb.status}
                        onChange={(e) => handleStatusChange(fb.id, e.target.value)}
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s}>
                            {STATUS_LABELS[s]}
                          </option>
                        ))}
                      </select>
                      <button
                        className={styles.deleteBtn}
                        onClick={() => handleDelete(fb.id)}
                      >
                        删除
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* 分页 */}
      {feedbacks.length >= PAGE_SIZE && (
        <div className={styles.pagination}>
          <button disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            上一页
          </button>
          <span>第 {page + 1} 页</span>
          <button
            disabled={feedbacks.length < PAGE_SIZE}
            onClick={() => setPage((p) => p + 1)}
          >
            下一页
          </button>
        </div>
      )}
    </div>
  );
}

export default FeedbackManagement;
